import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { IChoiceItem } from './ChoiceItem';
import { ChoiceList } from './ChoiceList';
import './EitherOrDialog.less';
import classNames from 'classnames';
import { IDialogOptions } from 'components/Dialogs/EitherOrDialog/typings';
import { usePortalResize } from 'hooks/usePortalResize';
import { convertChoicesToButtons, getSideBarPosition } from './EitherOrDialogUtils';
import { debounce } from 'debounce';
import { mergeRefs } from 'utils/Refs';
import { Dialog } from 'components/Dialogs/Dialog';
import { IDialogResizableBoxProps } from '../DialogResizableBox';
import { DialogTitleComponentProps } from '../DialogTitle';
import { dialogWindowSelector } from '../DialogUtil';
import { DialogWithSubtitle } from './DialogTitleWithSubtitle';

export interface IEitherOrDialogProps extends IEitherOrBasedDialogProps {
  /**
   * Hides title bar. It is useful in case custom title should be displayed.
   * @default false
   */
  hideTitleBar?: boolean;
  children: React.ReactNode;
  currentChoiceId?: string | null;
  closeOnEscape?: boolean;
  choiceItems: IChoiceItem[];
  className?: string;
  /**
   * Disables sidebar default beahvior when it flips to opposite side when there is no room
   * or goes to buttons panel when there is no room on both sides.
   *
   * @default false
   */
  disableSidebarFlip?: boolean;
  onClose?: () => void;  
  title: string;
  path?: string;
  components?: {
    dialogBox?: React.FC<IDialogResizableBoxProps>;
    title?: React.FC<DialogTitleComponentProps>;
  };
}

export interface IEitherOrBasedDialogProps {
  onItemChoosen: (item: IChoiceItem) => void;
  onInitialized?: () => void;
}

const CHOICE_WIDTH = 400;
const CHOICE_MIN_WIDTH = 260;
export const SIDEBAR_WIDTH = 245;
const DIALOG_MIN_HEIGHT = 600;
const SIDEBAR_MIN_HEIGHT_TWO_CHOICES = 630;
const SIDEBAR_MIN_HEIGHT_SINGLE_CHOICE = 323;
const DIALOG_LOW_WIDTH_BREAKPOINT = 1300;
const DIALOG_LOW_HEIGHT_BREAKPOINT = 684;

/**
 * Right now It is only content of EitherOrDialog.
 * It will be dialog when we will move dialog from MVC project to FrontEnd project.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export const EitherOrDialog = React.forwardRef(
  (
    {
      hideTitleBar,
      choiceItems,
      currentChoiceId,
      children,
      className,
      disableSidebarFlip = false,
      components,
      onItemChoosen,
      onInitialized,
      onClose,
      closeOnEscape,
      title,
      path,
    }: IEitherOrDialogProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const currentChoice = choiceItems.find(item => item.id === currentChoiceId);
    const [sidebarPosition, setSidebarPosition] = useState<'left' | 'right' | 'hidden'>(
      getSideBarPosition(currentChoice || choiceItems[0], choiceItems)
    );
    const contentRef = useRef<HTMLDivElement>(null);
    const choiceListRef = useRef<HTMLUListElement>(null);
    const alternativeChoices = useMemo(() => choiceItems.filter(item => item !== currentChoice), [
      choiceItems,
      currentChoice,
    ]);
    const isSideBarHidden = sidebarPosition === 'hidden';
    const [showSideBarIllustrations, setShowSideBarIllustraions] = useState<boolean>(true);
    const [dialogOptions, setDialogOptions] = useState<IDialogOptions>({ title });
    const [isLowWidth, setIsLowWidth] = useState(false);
    const [isLowHeight, setIsLowHeight] = useState(false);

    const handleChoose = useCallback(
      (item: IChoiceItem) => {
        if (item.handle) {
          const changeContent = item.handle(item);

          if (!changeContent) {
            return;
          }
        }
        onItemChoosen(item);
        setSidebarPosition(getSideBarPosition(item, choiceItems));
      },
      [choiceItems, onItemChoosen]
    );

    const updateSidebar = useCallback(() => {
      if (contentRef.current != null && !IS_TEST) {
        if (currentChoice == null) {
          return;
        }

        const windowRect = window.document.body.getClientRects()[0];
        // used dialogWindowSelector instead of current content, because of possibility to override content via dialogBox
        const dialogRect = contentRef.current.closest(dialogWindowSelector)!.getClientRects()[0];
        const sideBarRect = choiceListRef.current?.getClientRects()[0];
        const rightFreeSpace = windowRect.width - dialogRect.left - dialogRect.width;

        if (!disableSidebarFlip) {
          if (rightFreeSpace > SIDEBAR_WIDTH && dialogRect.left > SIDEBAR_WIDTH) {
            setSidebarPosition(getSideBarPosition(currentChoice, choiceItems));
          } else if (rightFreeSpace < SIDEBAR_WIDTH) {
            if (dialogRect.left < SIDEBAR_WIDTH) {
              setSidebarPosition('hidden');
            } else {
              setSidebarPosition('left');
            }
          } else {
            setSidebarPosition('right');
          }
        }

        if (!isSideBarHidden) {
          const minHeight =
            alternativeChoices.length === 1 ? SIDEBAR_MIN_HEIGHT_SINGLE_CHOICE : SIDEBAR_MIN_HEIGHT_TWO_CHOICES;

          setShowSideBarIllustraions((sideBarRect?.height ?? 0) >= minHeight);
        }

        if (DIALOG_LOW_WIDTH_BREAKPOINT < dialogRect.width + SIDEBAR_WIDTH) {
          setIsLowWidth(false);
        } else {
          setIsLowWidth(true);
        }
        if (DIALOG_LOW_HEIGHT_BREAKPOINT < dialogRect.height) {
          setIsLowHeight(false);
        } else {
          setIsLowHeight(true);
        }
      }
    }, [currentChoice, disableSidebarFlip, isSideBarHidden, choiceItems, alternativeChoices.length]);

    useEffect(() => {
      onInitialized?.();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateSidebarPositionDebounced = useMemo(() => debounce(updateSidebar, 200), [updateSidebar]);

    useEffect(() => {
      let choiceOptions: IDialogOptions;

      if (currentChoice != null) {
        choiceOptions = {
          width: currentChoice.dialogOptions?.width,
          height: currentChoice.dialogOptions?.height,
          minWidth: currentChoice.dialogOptions?.minWidth,
          resizable: currentChoice.dialogOptions?.resizable,
          margin: currentChoice.dialogOptions?.margin,
          useManualResize: currentChoice.dialogOptions?.useManualResize,
        };
        if (currentChoice.dialogOptions?.position) {
          choiceOptions.position = currentChoice.dialogOptions?.position;
        } else {
          choiceOptions.position = getDialogCenterPosition(
            currentChoice.dialogOptions?.width,
            currentChoice.dialogOptions?.height
          );
        }
      } else {
        choiceOptions = {
          width: choiceItems.length * CHOICE_WIDTH,
          height: DIALOG_MIN_HEIGHT,
          minWidth: choiceItems.length * CHOICE_MIN_WIDTH,
          resizable: false,
        };
      }

      setDialogOptions({
        title,
        ...choiceOptions,
        ...{
          minHeight: Math.max(DIALOG_MIN_HEIGHT, (currentChoice?.dialogOptions?.minHeight ?? 0) as number),
          buttons: [...(currentChoice?.dialogOptions?.buttons ?? [])],
          // NOTE: we need to use 'dp-no-content-margin' class to place "choice list" on the side.
          dialogClass: classNames(
            className,
            currentChoice?.dialogOptions?.dialogClass,
            'dpc-either-or-dialog',
            'dp-no-content-margin',
            {
              'dpc-either-or-dialog--no-subtitle': choiceItems.length <= 1,
            }
          ),
        },
      });
      updateSidebarPositionDebounced();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alternativeChoices, choiceItems.length, className, currentChoice, handleChoose, title, isSideBarHidden]);

    usePortalResize(updateSidebarPositionDebounced);

    const getDialogActions = useMemo(() => {
      if (sidebarPosition === 'hidden') {
        const choiceActions = convertChoicesToButtons(alternativeChoices, handleChoose) ?? [];
        return [...choiceActions, ...(dialogOptions.buttons ?? [])];
      }
      return dialogOptions.buttons;
    }, [alternativeChoices, dialogOptions.buttons, handleChoose, sidebarPosition]);

    return (
      <Dialog
        components={{
          dialogBox: components?.dialogBox,
          title: components?.title ?? (props => (
                <DialogWithSubtitle
                  {...props}
                  subtitle={path !== undefined ? path : currentChoice?.title}
                />
            )),
        }}
        holderClassName="dpc-either-or-dialog"
        className={getDialogClassNames(dialogOptions, isLowWidth, isLowHeight)}
        onClose={onClose}
        open
        disableFullScreenAction
        hideTitle={hideTitleBar}
        keyboard={{ closeOnEscapeKey: closeOnEscape ?? false }}
        onResize={updateSidebarPositionDebounced}
        onResizeStart={updateSidebarPositionDebounced}
        onResizeStop={updateSidebarPositionDebounced}
        onDrag={updateSidebarPositionDebounced}
        onDragStart={updateSidebarPositionDebounced}
        onDragStop={updateSidebarPositionDebounced}
        isResizeDisabled={!dialogOptions.resizable}
        dialogTitle={{ titleText: dialogOptions.title }}
        handleWindowResize={dialogOptions.useManualResize ? false : true}
        styles={{
          width: dialogOptions.width,
          height: dialogOptions.height,
          minWidth: dialogOptions.minWidth,
          minHeight: dialogOptions.minHeight,
          margin: dialogOptions.margin,
        }}
        dialogPosition={
          dialogOptions.position ? { top: dialogOptions.position?.y, left: dialogOptions.position?.x } : undefined
        }
        dialogActions={{
          actions: getDialogActions,
        }}
      >
        <div className={'dpc-either-or-dialog__content full-size'} ref={mergeRefs([contentRef, ref])}>
          {children}
          <ChoiceList
            showIllustrations={currentChoice == null ? true : showSideBarIllustrations}
            ref={choiceListRef}
            className={classNames({
              'dpc-either-or-dialog__sidebar': currentChoice != null,
              'dpc-either-or-dialog__sidebar--hidden': currentChoice != null && sidebarPosition === 'hidden',
              'dpc-either-or-dialog__sidebar--left': currentChoice != null && sidebarPosition === 'left',
              'dpc-either-or-dialog__sidebar--right': currentChoice != null && sidebarPosition === 'right',
            })}
            items={alternativeChoices}
            onChoose={handleChoose}
            isSideBar={choiceItems.some(item => item === currentChoice)}
          />
          {choiceItems.length === 0 && <div className="full-size flex-centering">There is no options to choose</div>}
        </div>
      </Dialog>
    );
  }
);

function getDialogClassNames(
  dialogOptions: IDialogOptions,
  isLowWidth: boolean,
  isLowHeight: boolean
): string | undefined {
  return classNames(
    'dpc-either-or-dialog__root',
    {
      'dpc-either-or-dialog__root--low-width': isLowWidth,
    },
    {
      'dpc-either-or-dialog__root--low-height': isLowHeight,
    },
    dialogOptions.dialogClass
  );
}

function getDialogCenterPosition(x?: number, y?: number) {
  if (y && x) {
    let yPosition = (window.innerHeight - y) / 2;
    if (yPosition < 0) {
      yPosition = 1;
    }
    return {
      x: (window.innerWidth - x) / 2,
      y: yPosition,
    };
  }
  return undefined;
}
